import { store } from '../store';
import {
  hasUpdateAtom,
  updateVersionAtom,
  isCheckingUpdateAtom,
} from '../store/update/updateState';
import { BUILD_TIME_PATH, probeAppHost } from '../store/network/networkState';

class UpdateService {
  private cachedVersion: string | null = null;
  private readonly VERSION_KEY = 'app_version';

  constructor() {
    // Load cached version from localStorage on startup
    this.loadCachedVersion();
  }

  private getBaseDomainUrl(): string {
    // Extract base domain from current location, removing any CDN path
    const { protocol, hostname, port } = window.location;
    const portSuffix = port ? `:${port}` : '';
    // Add current query parameter to prevent caching issues
    return `${protocol}//${hostname}${portSuffix}${BUILD_TIME_PATH}?v=${Date.now()}`;
  }

  private loadCachedVersion(): void {
    try {
      const cached = localStorage.getItem(this.VERSION_KEY);
      if (cached) {
        this.cachedVersion = cached;
      }
    } catch (error) {
      console.warn('Error loading cached version:', error);
    }
  }

  private saveCachedVersion(version: string): void {
    try {
      localStorage.setItem(this.VERSION_KEY, version);
      this.cachedVersion = version;
    } catch (error) {
      console.warn('Error saving version to localStorage:', error);
    }
  }

  async checkForUpdate(): Promise<boolean> {
    try {
      store.set(isCheckingUpdateAtom, true);

      // Fetch from base domain (server will return index.html by default)
      const response = await fetch(this.getBaseDomainUrl(), {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Parse the build-time.json response
      const data = await response.json();
      const newVersion = data.version;
      if (!newVersion) {
        throw new Error('Invalid version data received from server.');
      }

      // Store the new version info
      store.set(updateVersionAtom, newVersion);

      // If we have no cached version, cache this one and return false (no update)
      if (this.cachedVersion === null) {
        this.saveCachedVersion(newVersion);
        store.set(hasUpdateAtom, false);
        return false;
      }

      // Check if there's an update by comparing versions
      const hasUpdate = this.cachedVersion !== newVersion;
      store.set(hasUpdateAtom, hasUpdate);

      if (hasUpdate) {
        console.log('Update available:', {
          currentVersion: this.cachedVersion.substring(0, 16) + '...',
          newVersion: newVersion.substring(0, 16) + '...',
        });
      }

      return hasUpdate;
    } catch (error) {
      console.error('Error checking for update:', error);
      store.set(hasUpdateAtom, false);
      return false;
    } finally {
      store.set(isCheckingUpdateAtom, false);
    }
  }

  async updateToNewVersion(): Promise<void> {
    // Update cached version to acknowledge the update
    const newVersion = store.get(updateVersionAtom);
    if (newVersion) {
      this.saveCachedVersion(newVersion);
    }
    store.set(hasUpdateAtom, false);

    // Check if we're on a page that might have Dexie database operations
    // If so, navigate to home first
    const currentPath = window.location.pathname;
    const isDatabasePage =
      currentPath.includes('/inspection') ||
      currentPath.includes('/form') ||
      currentPath.includes('/bucket');

    // Proceed with invalidating service worker and clearing cache
    try {
      await this.invalidateSWAndClearCache();
      // Delay reload slightly to give SW time to settle
      setTimeout(() => {
        if (isDatabasePage) {
          // If we navigated away, ensure we go to home
          window.location.href = '/';
          return;
        }
        window.location.reload();
      }, 100);
    } catch (error) {
      console.error('Error during update process:', error);
      // Optionally, you could show an error message to the user here
    }
  }

  // The general online status also counts public DNS checks, so it can be true while the app
  // host is down. An update clears the service-worker caches and reloads from that host, so
  // the caller checks the host itself answers first, via the shared probe (HEAD /build-time.json).
  async isAppHostReachable(): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
      return await probeAppHost(controller.signal);
    } catch (error) {
      console.warn('App host check failed:', error);
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async invalidateSWAndClearCache(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames
            .filter((cacheName) => cacheName !== 'photos')
            .map((name) => caches.delete(name)),
        );
      }
      // Removed setTimeout for reload; caller should handle reload after awaiting this.
    } catch (err) {
      console.error('Error invalidating SW and clearing cache:', err);
      throw err; // Re-throw error to allow the caller to handle it.
    }
  }

  initializeCachedVersion(): void {
    // Check for updates on app start (uses cached version from localStorage)
    this.checkForUpdate().catch((error) => {
      console.error('Error initializing update service:', error);
    });
  }
}

export const updateService = new UpdateService();
